import { format } from "date-fns";
import { computeFromToForEnum } from "@/shared/utils/dateRangeFilterUtils";
import { getServerCalendarDate, getServerTimezone } from "@/shared/utils/serverTimeContext";

/**
 * Format a date string (YYYY-MM-DD) as a short human-readable date, e.g. "1 Feb 2026"
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string e.g. "1 Feb 2026", or the original string if invalid
 */
/** Short month abbreviations for locale-independent formatting. */
const _MONTHS_SHORT_FOR_DATE = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const formatShortDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString + "T00:00:00");
  if (isNaN(date.getTime())) return dateString;
  // Locale-independent: never toLocaleDateString (locale-dependent output varies by browser).
  const day = date.getDate();
  const mon = _MONTHS_SHORT_FOR_DATE[date.getMonth()];
  const yr = date.getFullYear();
  return `${day} ${mon} ${yr}`;
};

/**
 * Format a date string as a short human-readable date with weekday, e.g. "Sat 2 Sep 2023"
 * @param dateString - Date string (YYYY-MM-DD or ISO 8601)
 * @returns Formatted date string e.g. "Sat 2 Sep 2023", or the original string if invalid
 */
const _DAYS_SHORT_FOR_DATE = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const formatShortDateWithWeekday = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = new Date(dateString.length === 10 ? dateString + "T00:00:00" : dateString);
  if (isNaN(date.getTime())) return dateString;
  // Locale-independent: never toLocaleDateString (locale-dependent output varies by browser).
  const dow = _DAYS_SHORT_FOR_DATE[date.getDay()];
  const day = date.getDate();
  const mon = _MONTHS_SHORT_FOR_DATE[date.getMonth()];
  const yr = date.getFullYear();
  return `${dow} ${day} ${mon} ${yr}`;
};

/**
 * Format an ISO 8601 datetime string (or any value accepted by `new Date()`) as a short
 * weekday + day + month string WITHOUT the year, e.g. "Thu 9 Apr".
 *
 * Used in the Late Trips table "Date" column to match the legacy app display.
 *
 * @param isoStr - ISO 8601 string or null/undefined.
 * @param fallback - Value returned when the input is absent or unparseable (default: '—')
 * @returns Formatted string e.g. "Thu 9 Apr", or the fallback value.
 */
export const formatShortDateWeekdayNoYear = (
  isoStr: string | null | undefined,
  fallback = "—"
): string => {
  if (!isoStr) return fallback;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return fallback;
  // Locale-independent: never toLocaleDateString (locale-dependent output varies by browser).
  const dow = _DAYS_SHORT_FOR_DATE[d.getDay()];
  const day = d.getDate();
  const mon = _MONTHS_SHORT_FOR_DATE[d.getMonth()];
  return `${dow} ${day} ${mon}`;
};

/**
 * Normalise a time string to HH:mm:ss with leading zeros.
 * Accepts HH:mm or HH:mm:ss format.
 * @param timeStr - Time string e.g. "8:30" or "08:30:00"
 * @returns Normalised time string e.g. "08:30:00"
 */
export const normaliseTimeStr = (timeStr: string): string => {
  if (!timeStr) return "00:00:00";
  const trimmed = timeStr.trim();

  // Accept legacy HHMM / HHMMSS formats with no colons (e.g. "0000",
  // "2359", "235959"). These come from RoR / saved filtersets where the
  // colon was stripped before persisting. Without this guard,
  // `normaliseTimeStr('0000')` returned '0000:00:00' (invalid) and the
  // subscriptions Date Range filter failed at the K3 layer (QA #2105).
  if (!trimmed.includes(":") && /^\d{4,6}$/.test(trimmed)) {
    const hour = trimmed.slice(0, 2);
    const minute = trimmed.slice(2, 4);
    const second = trimmed.slice(4, 6) || "00";
    return `${hour}:${minute}:${second}`;
  }

  const parts = trimmed.split(":");
  const hour = (parts[0] || "0").padStart(2, "0");
  const minute = (parts[1] || "0").padStart(2, "0");
  const second = (parts[2] || "0").padStart(2, "0");
  return `${hour}:${minute}:${second}`;
};

/**
 * Format a Date object as a local datetime string (YYYY-MM-DDTHH:mm:ss),
 * optionally overriding the time portion with a pre-formatted time string.
 * Uses local time components to avoid UTC offset issues with toISOString().
 * @param date - The Date to format
 * @param time - Optional pre-formatted time override in HH:mm:ss format
 * @returns Local datetime string e.g. "2026-03-27T08:30:00"
 */
export const formatLocalDate = (date: Date, time?: string): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const timeStr =
    time ??
    `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
  return `${year}-${month}-${day}T${timeStr}`;
};

/**
 * Format an ISO 8601 timestamp (or date string) as a local-time HH:mm string.
 *
 * Uses the browser's local timezone (same as the existing Ecolane app) so that
 * time values displayed in the UI match what dispatchers see in the legacy system.
 *
 * @param isoStr - ISO 8601 string or any value accepted by `new Date()`.
 *                 Pass `null` or `undefined` to receive the fallback.
 * @param fallback - Value returned when the input is absent or unparseable (default: '—')
 * @returns Local time as "HH:mm" string, e.g. "15:30"
 */
export const formatTimeHHMM = (isoStr: string | null | undefined, fallback = "—"): string => {
  if (!isoStr) return fallback;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return fallback;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

/**
 * Format an ISO 8601 datetime string (or any value accepted by `new Date()`) as a
 * locale-aware 24-hour "HH:MM" time string, matching the browser's local timezone.
 *
 * Equivalent to `new Date(str).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })`.
 * Centralises this pattern so it can be reused without duplicating try/catch boilerplate.
 *
 * @param dateTimeStr - ISO datetime string or null.
 * @param fallback    - Value returned when the input is absent or unparseable (default: '-')
 * @returns Locale-formatted 24h time string e.g. "09:30", or the fallback value.
 */
export const formatTimeLocale = (
  dateTimeStr: string | null | undefined,
  fallback = "-"
): string => {
  if (!dateTimeStr) return fallback;
  try {
    const formatted = new Date(dateTimeStr).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    // toLocaleTimeString can return "Invalid Date" or throw — treat both as fallback
    return formatted === "Invalid Date" ? fallback : formatted;
  } catch {
    return fallback;
  }
};

/**
 * Create ISO timestamp from date and time strings, treating input as UTC
 * This prevents timezone conversion issues when booking trips
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format
 * @returns ISO 8601 timestamp string (e.g., "2026-03-18T12:12:00.000Z")
 */
export const createUTCTimestamp = (date: string, time: string): string => {
  // Directly format as UTC without timezone conversion
  // This treats user input as already being in UTC
  return `${date}T${time}:00.000Z`;
};

/**
 * Extract date and time from ISO timestamp as UTC values
 * This is the reverse of createUTCTimestamp
 * @param isoString - ISO 8601 timestamp string
 * @returns Object with date (YYYY-MM-DD) and time (HH:mm) in UTC
 */
export const extractUTCDateTime = (isoString: string): { date: string; time: string } => {
  const dateObj = new Date(isoString);

  // Use UTC methods to prevent timezone conversion
  const year = dateObj.getUTCFullYear();
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getUTCDate()).padStart(2, "0");
  const hours = String(dateObj.getUTCHours()).padStart(2, "0");
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");

  return {
    date: `${year}-${month}-${day}`,
    time: `${hours}:${minutes}`,
  };
};

export const getTodayTimeRange = (): { start: string; end: string } => {
  // Use server calendar date so filters are consistent across user timezones
  const { year, month, day } = getServerCalendarDate();
  const yyyy = String(year);
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");

  const start = `${yyyy}-${mm}-${dd}T00:00:00`;
  const end = `${yyyy}-${mm}-${dd}T23:59:59`;

  return { start, end };
};

export const calculateAge = (dobString?: string): string => {
  if (!dobString) return "-";
  // Guard against UTC-midnight shift: bare "YYYY-MM-DD" strings parsed by
  // new Date() are treated as UTC midnight, shifting the date to the previous
  // day in non-UTC browsers (e.g. ET). Append T12:00:00 to keep the calendar
  // date stable across all timezones (same guard used in useFormat.formatDate).
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dobString) ? `${dobString}T12:00:00` : dobString;
  const dob = new Date(normalized);
  if (isNaN(dob.getTime())) return "-";

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age.toString();
};

/**
 * Builds a date range query for API filtering based on time period selection
 * @param timePeriod - The selected time period (e.g., 'date_range', 'date')
 * @param fromDate - The start date (YYYY-MM-DD format) for 'date_range' selection
 * @param toDate - The end date (YYYY-MM-DD format) for 'date_range' selection
 * @param singleDate - The single date (YYYY-MM-DD format) for 'date' selection
 * @param fieldName - The API field name to filter on (default: 'create_ts')
 * @param fromTime
 * @param toTime
 * @returns An array with the query or null if no date filter should be applied
 */
export const buildDateRangeQuery = (
  timePeriod: string | undefined,
  fromDate: string | undefined,
  toDate: string | undefined,
  singleDate: string | undefined,
  fieldName: string = "create_ts",
  fromTime?: string,
  toTime?: string
): unknown[] | null => {
  const fromTimeStr = normaliseTimeStr(fromTime || "00:00");
  const toTimeStr = normaliseTimeStr(toTime || "23:59");

  // Handle date range filtering
  if (timePeriod === "date_range" && fromDate && toDate) {
    const fromDateTime = `${fromDate}T${fromTimeStr}`;
    const toDateTime = `${toDate}T${toTimeStr}`;
    return ["between", fieldName, fromDateTime, toDateTime];
  }

  if (timePeriod === "date_range" && fromDate && !toDate) {
    const fromDateTime = `${fromDate}T${fromTimeStr}`;
    return ["gte", fieldName, fromDateTime];
  }
  if (timePeriod === "date_range" && !fromDate && toDate) {
    const toDateTime = `${toDate}T${toTimeStr}`;
    return ["lte", fieldName, toDateTime];
  }

  // Handle single date filtering (entire day)
  if (timePeriod === "date" && singleDate) {
    const fromDateTime = `${singleDate}T${fromTimeStr}`;
    const toDateTime = `${singleDate}T${toTimeStr}`;
    return ["between", fieldName, fromDateTime, toDateTime];
  }
  if (timePeriod) {
    const computed = computeFromToForEnum(timePeriod);
    if (computed) {
      return computed.to
        ? ["between", fieldName, computed.from, computed.to]
        : ["gte", fieldName, computed.from];
    }
  }

  return null;
};

/**
 * Format a Date (or ISO string) as a human-readable datetime string: "13 Apr 2026 13:47"
 * Uses en-GB locale with 24-hour time, no AM/PM.
 * @param date - Date object or ISO 8601 string
 * @returns Formatted string e.g. "13 Apr 2026 13:47", or '' if invalid
 */
export const formatDateTimeDisplay = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  // Locale-independent: never toLocaleDateString (locale-dependent output varies by browser).
  const day = d.getDate();
  const mon = _MONTHS_SHORT_FOR_DATE[d.getMonth()];
  const yr = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${mon} ${yr} ${hh}:${mm}`;
};

/**
 * Format an ISO 8601 timestamp for export, stripping sub-second precision and timezone
 * to match the legacy export format (e.g. "2026-04-08T02:52:36").
 * @param isoStr - ISO 8601 string or null
 * @returns Formatted datetime string e.g. "2026-04-08T02:52:36", or '' if input is null/empty
 */
export const formatISOForExport = (isoStr: string | null): string => {
  if (!isoStr) return "";
  const match = isoStr.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : isoStr;
};

/**
 * Format a duration in milliseconds as a human-readable "X h Y min" string.
 *
 * Optionally subtracts a threshold (in minutes) before formatting — used to display
 * lateness beyond a configured late threshold, matching the legacy k3/Rails behaviour.
 *
 * Uses integer division (floor) to truncate sub-minute precision, consistent with
 * the k3 backend's pickup_late_sec / dropoff_late_sec computation.
 *
 * @param diffMs      - Duration in milliseconds (e.g. actualArrival - scheduledTime).
 * @param thresholdMin - Minutes to subtract before formatting (default: 0).
 * @param fallback    - Value returned when the result is zero or negative (default: '—').
 * @returns Formatted string e.g. "1 h 15 min", "45 min", "2 h", or the fallback.
 */
/**
 * Format a duration in seconds as a human-readable "X h Y min" string.
 *
 * e.g. 600 → "10 min", 3600 → "1 h", 5100 → "1 h 25 min"
 *
 * @param seconds  - Duration in seconds (e.g. flexibility_minus / flexibility_plus API values).
 * @param fallback - Value returned when seconds is null/undefined/0 (default: '—').
 * @returns Formatted string e.g. "10 min", "1 h", "1 h 25 min", or the fallback.
 */
export const formatDurationFromSeconds = (
  seconds: number | null | undefined,
  fallback = "—"
): string => {
  if (seconds === null || seconds === undefined) return fallback;
  const totalMins = Math.floor(seconds / 60);
  if (totalMins <= 0) return fallback;
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${totalMins} min`;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} min`;
};

/**
 * Format a duration in seconds as a human-readable "X h Y min" / "Xs" string.
 * Uses Rails `round_to(value, 60)` (nearest-60-second rounding) to match the
 * Rails `:duration` display type used in the Schedule Versions summary footer
 * and per-row duration columns (Ideal, Combined, Padding, Onboard Time, etc.).
 *
 * Rails formula (`Unit::Duration.format_display_open`):
 *   mod = value % 60
 *   if mod >= 30: value + (60 - mod)   → round UP to next minute
 *   else:         value - mod          → round DOWN to previous minute
 *
 * Behaviour:
 *  - `secs <= 0` or falsy → `"—"`
 *  - `secs < 60` after rounding → `"Xs"` (sub-minute)
 *  - `secs >= 60` → `"N min"`, `"N h"`, or `"N h M min"`
 *
 * Worked examples (matching Rails exactly):
 *   2700 sec (45 min exactly)  → mod=0  < 30  → 2700  → "45 min"
 *   2730 sec (45.5 min)        → mod=30 ≥ 30  → 2760  → "46 min"  ← key fix
 *   2729 sec                   → mod=29 < 30  → 2700  → "45 min"
 *   90 sec                     → mod=30 ≥ 30  → 120   → "2 min"
 *   89 sec                     → mod=29 < 30  → 60    → "1 min"
 *
 * @param secs     - Duration in seconds.
 * @param fallback - Returned when secs is falsy / ≤ 0 (default: `"—"`).
 * @returns Formatted duration string.
 *
 * @example
 * formatDurationSec(45)    // => "45s"
 * formatDurationSec(600)   // => "10 min"
 * formatDurationSec(3600)  // => "1 h"
 * formatDurationSec(5100)  // => "1 h 25 min"
 * formatDurationSec(2730)  // => "46 min"  (not "45 min" — round_to rounds up at 30s)
 * formatDurationSec(0)     // => "—"
 */
export const formatDurationSec = (secs: number, fallback = "—"): string => {
  if (!secs || secs <= 0) return fallback;
  const abs = Math.abs(secs); // always positive (durations are non-negative, but guard for safety)

  // Rails round_to(value, 60): nearest-60-second rounding (half-up at 30 sec)
  const mod = abs % 60;
  const rounded = mod >= 30 ? abs + (60 - mod) : abs - mod;

  if (rounded < 60) return `${Math.round(rounded)}s`;
  const totalMins = Math.round(rounded / 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};

export const formatDurationFromMs = (diffMs: number, thresholdMin = 0, fallback = "—"): string => {
  const totalMin = Math.floor(diffMs / (1000 * 60)) - thresholdMin;
  if (totalMin <= 0) return fallback;
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} min`;
};

/**
 * Extracts a YYYY-MM-DD string from the date portion of an ISO datetime string.
 * Fast-paths when the first 10 characters already form a valid date.
 * Falls back to Date parsing for non-ISO formats.
 *
 * @param iso - ISO datetime string (e.g. "2026-04-08T10:30:00"), a plain date string,
 *              or null/undefined.
 * @returns "YYYY-MM-DD" string, or null when the input is falsy or unparseable.
 */
export const extractDateYMD = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const part = iso.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Generates a filename-safe timestamp in Eastern Time for CSV export filenames.
 * Produces the format: YYYYMMDD_HHmm_TZ  (e.g. "20260414_0337_EDT")
 *
 * Follows the established codebase pattern (BatchJobEventsPage, TripEventsPage, etc.):
 * 1. Converts current time to Eastern Time via toLocaleString re-parse
 * 2. Formats the date/time portion with date-fns
 * 3. Appends the short timezone abbreviation (EDT/EST) from Intl
 *
 * @returns Filename-safe timestamp string e.g. "20260414_0337_EDT"
 */
export const getExportTimestamp = (): string => {
  const now = new Date();

  // Convert to Eastern Time using the established codebase pattern
  const edtDateString = now.toLocaleString("en-US", { timeZone: "America/New_York" });
  const edtDate = new Date(edtDateString);
  const formattedDate = format(edtDate, "yyyyMMdd_HHmm");

  // Get the short timezone abbreviation (e.g. "EDT" in summer, "EST" in winter)
  const tzAbbr =
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      timeZoneName: "short",
    })
      .formatToParts(now)
      .find((p) => p.type === "timeZoneName")?.value ?? "ET";

  return `${formattedDate}_${tzAbbr}`;
};

// ---------------------------------------------------------------------------
// Eastern Time (America/New_York) utilities
//
// Centralises all EDT/EST conversion and formatting so that every part of the
// codebase that needs to work in Eastern Time uses a single, consistent
// implementation instead of duplicating `toLocaleString` / `Intl.DateTimeFormat`
// patterns inline.
//
// The k3 backend stores and returns timestamps as bare Eastern-time strings
// (no timezone suffix), e.g. "2026-04-16T13:47:00". All `create_ts`, `update_ts`
// and similar API fields must therefore be formatted in Eastern Time to round-trip
// correctly with the server.
//
// Usage:
//   import { formatEasternTimestamp, formatEasternDateString } from '@/shared/utils/dateUtils';
// ---------------------------------------------------------------------------

/** IANA timezone identifier for US Eastern Time (handles EDT/EST automatically). */
export const EASTERN_TIMEZONE = "America/New_York";

interface EasternParts {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
  /** Short timezone abbreviation, either 'EDT' (summer) or 'EST' (winter). */
  tzAbbr: string;
}

/**
 * Internal: decompose a Date into its Eastern Time calendar/clock parts using
 * `Intl.DateTimeFormat.formatToParts()`. More reliable than the `toLocaleString`
 * re-parse trick because it never depends on the browser's Date constructor
 * being able to parse a locale-formatted string.
 */
const getEasternParts = (date: Date): EasternParts => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EASTERN_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "short",
  }).formatToParts(date);

  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "";

  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    // Some browsers return '24' for midnight in hour12:false mode — normalise to '00'
    hour: get("hour") === "24" ? "00" : get("hour"),
    minute: get("minute"),
    second: get("second"),
    tzAbbr: get("timeZoneName"), // 'EDT' or 'EST'
  };
};

/**
 * Returns the Eastern Time timezone abbreviation for a given date.
 * Returns `'EDT'` during Daylight Saving Time (mid-March → early-November)
 * and `'EST'` otherwise.
 *
 * @param date - The date to check. Defaults to `new Date()` (now).
 * @returns `'EDT'` or `'EST'`
 *
 * @example
 * getEasternTimezoneAbbr()                    // => 'EDT' (during summer)
 * getEasternTimezoneAbbr(new Date('2026-01-15')) // => 'EST'
 */
export const getEasternTimezoneAbbr = (date: Date = new Date()): string =>
  getEasternParts(date).tzAbbr;

/**
 * Returns a `Date` object whose **local** time components match the Eastern Time
 * representation of the input. Useful when passing to `date-fns` `format()`.
 *
 * > ⚠️ Use only for display/formatting — the UTC value of the returned Date
 * > differs from the input, so do not use it for arithmetic.
 *
 * @param date - The date to convert. Defaults to `new Date()` (now).
 *
 * @example
 * import { format } from 'date-fns';
 * format(toEasternDate(), 'yyyyMMdd_HHmm'); // => "20260416_0917"
 */
export const toEasternDate = (date: Date = new Date()): Date =>
  new Date(date.toLocaleString("en-US", { timeZone: EASTERN_TIMEZONE }));

/**
 * Parses a k3 Eastern Time stop timestamp into a local `Date` whose HH:mm:ss
 * matches the Eastern Time value.
 *
 * The k3 backend returns stop timestamps as bare ET strings with no timezone
 * suffix.  They arrive in two formats:
 *   - `"HH:mm:ss"`              — time-only (Eastern Time)
 *   - `"YYYY-MM-DDTHH:mm:ss"`   — ISO without TZ suffix (value is Eastern Time)
 *
 * Both are treated as ET time-of-day: the time components are extracted and
 * applied to today's LOCAL date via `setHours()`.  This keeps the value
 * consistent with [`getEasternHourMinute`] which builds `nowNoSec` the same way,
 * so comparisons like `stopTime >= from` work correctly in "ET-as-local space".
 *
 * @param ts - Stop timestamp string in `"HH:mm:ss"` or `"YYYY-MM-DDTHH:mm:ss"` format.
 * @returns A `Date` whose local hours/minutes/seconds match the ET stop time.
 *
 * @example
 * parseEasternStopTimestamp("10:52:00")               // local Date at 10:52:00
 * parseEasternStopTimestamp("2026-04-24T10:52:00")    // local Date at 10:52:00
 */
export const parseEasternStopTimestamp = (ts: string): Date => {
  const d = new Date();
  let h = 0,
    m = 0,
    s = 0;
  if (ts.includes("T")) {
    // "YYYY-MM-DDTHH:mm:ss" — extract the time portion after T
    const timePart = ts.split("T")[1];
    [h, m, s] = timePart.split(":").map(Number);
  } else {
    // "HH:mm:ss"
    [h, m, s] = ts.split(":").map(Number);
  }
  d.setHours(h, m, s ?? 0, 0);
  return d;
};

/**
 * Returns the current Eastern Time (America/New_York) broken out as integer
 * hour and minute values.
 *
 * Extends `toEasternDate()` using the same `toLocaleString` re-parse pattern but
 * exposes the numeric components directly so callers can use them without an
 * additional `getHours()` / `getMinutes()` call.
 *
 * @param date - The date to read. Defaults to `new Date()` (now).
 * @returns `{ hh, mm }` — Eastern Time hour (0–23) and minute (0–59).
 *
 * @example
 * const { hh, mm } = getEasternHourMinute();
 * // If current ET is 10:52 → { hh: 10, mm: 52 }
 */
export const getEasternHourMinute = (date: Date = new Date()): { hh: number; mm: number } => {
  const etHHMM = date.toLocaleString("en-US", {
    timeZone: EASTERN_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }); // e.g. "10:52"
  const [hStr, mStr] = etHHMM.split(":");
  const hh = Number(hStr);
  const mm = Number(mStr);
  // Normalise the rare browser quirk where midnight is returned as 24:00
  return { hh: hh === 24 ? 0 : hh, mm };
};

/**
 * Formats a date as a naive Eastern Time datetime string: `"YYYY-MM-DDTHH:mm:ss"`.
 *
 * No timezone suffix is appended, matching the format the k3 backend uses for
 * fields like `create_ts` and `update_ts`.
 *
 * @param date - The date to format. Defaults to `new Date()` (now).
 * @returns e.g. `"2026-04-16T09:17:42"`
 *
 * @example
 * formatEasternTimestamp()  // => "2026-04-16T09:17:42"
 */
export const formatEasternTimestamp = (date: Date = new Date()): string => {
  const { year, month, day, hour, minute, second } = getEasternParts(date);
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
};

/**
 * Formats a date as an Eastern Time date-only string: `"YYYY-MM-DD"`.
 *
 * @param date - The date to format. Defaults to `new Date()` (now).
 * @returns e.g. `"2026-04-16"`
 *
 * @example
 * formatEasternDateString()  // => "2026-04-16"
 */
export const formatEasternDateString = (date: Date = new Date()): string => {
  const { year, month, day } = getEasternParts(date);
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date as an Eastern Time time string: `"HH:mm"`.
 *
 * @param date - The date to format. Defaults to `new Date()` (now).
 * @returns e.g. `"09:17"`
 *
 * @example
 * formatEasternTimeHHMM()  // => "09:17"
 */
export const formatEasternTimeHHMM = (date: Date = new Date()): string => {
  const { hour, minute } = getEasternParts(date);
  return `${hour}:${minute}`;
};

/**
 * Generates a filename-safe Eastern Time timestamp: `"YYYYMMDD_HHmm_TZ"`.
 *
 * Consolidates the pattern used independently by `getExportTimestamp()` in
 * `dateUtils` and `generateExportFilename()` in `csvExport` into a single
 * shared implementation.
 *
 * @param date - The date to format. Defaults to `new Date()` (now).
 * @returns e.g. `"20260416_0917_EDT"`
 *
 * @example
 * formatEasternExportTimestamp()  // => "20260416_0917_EDT"
 */
export const formatEasternExportTimestamp = (date: Date = new Date()): string => {
  const { year, month, day, hour, minute, tzAbbr } = getEasternParts(date);
  return `${year}${month}${day}_${hour}${minute}_${tzAbbr}`;
};

/**
 * Lower-cased short month names.
 * Shared by `parseDDMMMYYYYToISO`, `formatCommentTime`, and `formatScheduleDate`.
 */
const SHORT_MONTHS_LOWER = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

/** Short day-of-week abbreviations (Sun-indexed) used by `formatScheduleDate`. */
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Locale-independent date formatter for the Schedule Details page and any other
 * place that needs a consistent "Mon 28 Apr 2026" / "28 Apr 2026" display format.
 *
 * Parses the date using explicit numeric extraction — never `toLocaleDateString`,
 * which is locale-dependent and silently shifts dates in non-US browser locales
 * (e.g. an IST +5:30 browser may produce a different day-of-week / day number).
 *
 * @param dateStr - ISO date or datetime string (e.g. `"2026-04-28"` or `"2026-04-28T14:30:00"`).
 * @param mode    - `'full'` (default) → `"Mon 28 Apr 2026"`;
 *                  `'short'` → `"28 Apr 2026"` (no weekday).
 * @returns Formatted date string, or `'-'` when the input is absent/unparseable.
 *
 * @example
 * formatScheduleDate("2026-04-28")           // => "Mon 28 Apr 2026"
 * formatScheduleDate("2026-04-28", 'short')  // => "28 Apr 2026"
 * formatScheduleDate(undefined)              // => "-"
 */
export const formatScheduleDate = (
  dateStr: string | undefined,
  mode: "full" | "short" = "full"
): string => {
  if (!dateStr) return "-";
  try {
    const isoDate = dateStr.split("T")[0];
    const parts = isoDate.split("-");
    if (parts.length !== 3) return dateStr;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-indexed
    const day = parseInt(parts[2], 10);
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return dateStr;
    const dayName = DAYS_SHORT[date.getDay()];
    const monthName = SHORT_MONTHS_LOWER[date.getMonth()];
    // Title-case the month abbreviation: "apr" → "Apr"
    const monthTitle = monthName.charAt(0).toUpperCase() + monthName.slice(1);
    const dayNum = date.getDate();
    const fullYear = date.getFullYear();
    if (mode === "short") return `${dayNum} ${monthTitle} ${fullYear}`;
    return `${dayName} ${dayNum} ${monthTitle} ${fullYear}`;
  } catch {
    return dateStr;
  }
};

/**
 * Parses a "D MMM YYYY" display date string (e.g. `"6 Apr 2026"`) back to the
 * ISO `"YYYY-MM-DD"` format required by the API.
 *
 * Returns an empty string when the input is absent or does not match the expected
 * three-part pattern, so callers can treat an empty return as a parse failure.
 *
 * @param dateStr - Display date string in "D MMM YYYY" format
 * @returns ISO date string e.g. `"2026-04-06"`, or `''` if invalid
 *
 * @example
 * parseDDMMMYYYYToISO("6 Apr 2026")  // => "2026-04-06"
 * parseDDMMMYYYYToISO("")             // => ""
 */
export const parseDDMMMYYYYToISO = (dateStr: string): string => {
  if (!dateStr) return "";
  const parts = dateStr.trim().split(/\s+/);
  if (parts.length !== 3) return "";
  const day = parts[0].padStart(2, "0");
  const monthIdx = SHORT_MONTHS_LOWER.indexOf(parts[1].toLowerCase());
  if (monthIdx === -1) return "";
  const month = String(monthIdx + 1).padStart(2, "0");
  const year = parts[2];
  if (!/^\d{4}$/.test(year)) return "";
  return `${year}-${month}-${day}`;
};

/**
 * Format a Date as a local ISO-like datetime string WITHOUT timezone offset.
 * Returns "YYYY-MM-DDTHH:mm:ss" using the local clock values.
 *
 * CRITICAL: Use this instead of `Date.toISOString()` for date parity with RoR.
 * `toISOString()` converts to UTC, shifting the date for non-UTC timezones.
 */
export const formatLocalDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Format a duration given in minutes as a human-readable "Xh Ym" string.
 *
 * Shows `"0 min"` when the value is 0, and returns the fallback (default `"-"`)
 * when the value is null or undefined.
 *
 * @param minutes  - Duration in minutes (e.g. the `free` field from run utilization data).
 * @param fallback - Value returned when minutes is null/undefined (default: '-').
 * @returns Formatted string e.g. "45 min", "1 h", "1 h 30 min", "0 min", or the fallback.
 *
 * @example
 * formatMinutesToHoursAndMinutes(0)    // => "0 min"
 * formatMinutesToHoursAndMinutes(45)   // => "45 min"
 * formatMinutesToHoursAndMinutes(90)   // => "1 h 30 min"
 * formatMinutesToHoursAndMinutes(120)  // => "2 h"
 * formatMinutesToHoursAndMinutes(null) // => "-"
 */
export const formatMinutesToHoursAndMinutes = (
  minutes: number | null | undefined,
  fallback = "-"
): string => {
  if (minutes === null || minutes === undefined) return fallback;
  const totalMins = Number(minutes);
  if (totalMins === 0) return "0 min";
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} min`;
};

/**
 * Format an ISO 8601 datetime string as "Weekday D Mon YYYY HH:mm" (24-hour).
 * e.g. "2026-04-05T07:22:00" → "Sun 5 Apr 2026 07:22"
 *
 * Combines formatShortDateWithWeekday + formatTimeHHMM. Used in the Info section
 * of the archive trip details page to match the legacy Rails app format.
 *
 * @param isoStr - ISO 8601 string or null/undefined.
 * @param fallback - Value returned when the input is absent or unparseable (default: '—').
 * @returns Formatted string e.g. "Sun 5 Apr 2026 07:22", or the fallback.
 */
export const formatDateTimeWithWeekday = (
  isoStr: string | null | undefined,
  fallback = "—"
): string => {
  if (!isoStr) return fallback;
  const datePart = formatShortDateWithWeekday(isoStr);
  const timePart = formatTimeHHMM(isoStr);
  if (datePart === "-" || timePart === "—") return fallback;
  return `${datePart} ${timePart}`;
};

/**
 * Extract "YYYY-MM-DD" from a datetime string.
 * Handles:
 *   - ISO / ISO-ish:  "2026-04-23T14:30:00"  or  "2026-04-23 14:30:00"
 *   - US display:     "04/23/2026"  (MM/DD/YYYY — the locale format used by the trips UI)
 */
export const extractDateIso = (dateTimeStr: string): string | null => {
  if (!dateTimeStr) return null;
  // ISO: starts with "YYYY-MM-DD"
  const isoMatch = dateTimeStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  // US display format "MM/DD/YYYY"
  const usMatch = dateTimeStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (usMatch) {
    const [, mm, dd, yyyy] = usMatch;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  return null;
};

/**
 * Format a raw promised_pickup ISO-ish string to a display time "HH:mm".
 * Uses string-splitting (not new Date()) to avoid timezone shifts on bare
 * Eastern-time strings like "2026-04-23 14:30:00".
 *
 * @param rawPickup - Raw datetime string e.g. "2026-04-23T14:30:00" or "2026-04-23 14:30:00"
 * @returns "HH:mm" string e.g. "14:30", or "-" when input is empty
 */
export const formatPickupTime = (rawPickup: string): string => {
  if (!rawPickup) return "-";
  const timePart = rawPickup.split(/T| /)[1];
  return timePart ? timePart.substring(0, 5) : rawPickup;
};

/**
 * Format a raw promised_pickup ISO-ish string to a display date MM/DD/YYYY,
 * matching the locale format used by the rest of the trips UI.
 *
 * @param iso - ISO or ISO-ish datetime string e.g. "2026-04-23T14:30:00" or "2026-04-23 14:30:00"
 * @returns Date string in "MM/DD/YYYY" format, or empty string when input is empty/invalid
 */
export const formatDateFromIso = (iso: string): string => {
  if (!iso) return "";
  try {
    const d = new Date(iso.replace(" ", "T"));
    if (isNaN(d.getTime())) return iso.split(/T| /)[0] ?? "";
    // Locale-independent: never toLocaleDateString (locale-dependent output varies by browser).
    // Produces MM/DD/YYYY format (matching the legacy trips UI expectation).
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  } catch {
    return iso.split(/T| /)[0] ?? "";
  }
};

/**
 * Format a route-comment timestamp for display as "Mon D, YYYY H:MM AM/PM".
 *
 * Timestamps from `route_comment` are naive Eastern Time strings (no TZ suffix),
 * e.g. `"2026-04-28T14:30:00"`. Slicing directly avoids the UTC conversion that
 * `new Date(ts).toLocaleString()` would introduce in non-US browser locales
 * (e.g. IST +5:30 shifts the displayed time by 5.5 hours).
 *
 * Reuses the existing `SHORT_MONTHS_LOWER` array (title-cased at lookup time) so
 * there is a single source of truth for month abbreviations in this file.
 *
 * Falls back to `Date`-based parsing for non-ISO timestamp formats.
 *
 * @param timestamp - Raw timestamp string from the k3 API, or undefined.
 * @returns Formatted string e.g. `"Apr 28, 2026 2:30 PM"`, or `'-'` when absent/invalid.
 *
 * @example
 * formatCommentTime("2026-04-28T14:30:00")  // => "Apr 28, 2026 2:30 PM"
 * formatCommentTime(undefined)              // => "-"
 */
/**
 * Format an absolute minute value as a human-readable duration string for
 * timetable deviation display (e.g. "5 min", "2 h 53 min", "45 sec").
 *
 * Uses `Math.ceil` — matching the Rails `:mf_timetable` ceiling-based rounding
 * so that "7 min 52 sec late" rounds UP to "8 min late" (not 7 min).
 *
 * Differences from `formatDurationSec`:
 *   - Input is **minutes** (fractional), not seconds.
 *   - Values < 1 min are shown as ceiling seconds: `Math.ceil(absMins × 60) sec`.
 *   - Uses `Math.ceil` throughout (not `Math.round`).
 *
 * @param absMins - Absolute deviation in minutes (must be ≥ 0).
 * @returns Formatted string e.g. `"45 sec"`, `"5 min"`, `"2 h 53 min"`, `"1 h"`.
 *
 * @example
 * formatDiffMinsCeil(0.5)    // => "30 sec"
 * formatDiffMinsCeil(5)      // => "5 min"
 * formatDiffMinsCeil(7.87)   // => "8 min"   (ceil, not round)
 * formatDiffMinsCeil(60)     // => "1 h"
 * formatDiffMinsCeil(173)    // => "2 h 53 min"
 */
export const formatDiffMinsCeil = (absMins: number): string => {
  if (absMins < 1) {
    return `${Math.ceil(absMins * 60)} sec`;
  }
  const totalMins = Math.ceil(absMins);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours > 0) {
    return `${hours} h${mins > 0 ? ` ${mins} min` : ""}`;
  }
  return `${totalMins} min`;
};

/**
 * Format a UTC `Date` as `"MM/DD/YYYY HH:MM TZ"` in the server's timezone.
 *
 * Matches the format Rails uses in ArchiveTimeValidator error messages:
 *   `strftime('%m/%d/%Y %H:%M %Z')` → e.g. "05/05/2026 14:10 EDT"
 *
 * Uses `getServerTimezone()` from `serverTimeContext` so the result always
 * reflects the server TZ (America/New_York by default) regardless of the
 * browser's local clock.
 *
 * @param date - A `Date` object (UTC instant) to format.
 * @returns e.g. `"05/05/2026 14:10 EDT"`
 */
export const formatDateTimeInServerTZ = (date: Date): string => {
  const tz = getServerTimezone();
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });
  const parts = fmt.formatToParts(date).reduce<Record<string, string>>((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  // Normalise midnight: some browsers return '24' for 00:00 in hour12:false mode
  const hour = parts.hour === "24" ? "00" : parts.hour;
  return `${parts.month}/${parts.day}/${parts.year} ${hour}:${parts.minute} ${parts.timeZoneName}`;
};

export const formatCommentTime = (timestamp: string | undefined): string => {
  if (!timestamp) return "-";
  /** Title-cases a lower-case 3-letter month abbreviation, e.g. "apr" → "Apr". */
  const toTitleMonth = (lower: string): string => lower.charAt(0).toUpperCase() + lower.slice(1);
  try {
    // Fast-path: ISO naive ET string — slice directly, no Date construction
    const isoMatch = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, yyyy, mm, dd, hh, min] = isoMatch;
      const rawMonth = SHORT_MONTHS_LOWER[parseInt(mm, 10) - 1] ?? mm;
      const monthName = toTitleMonth(rawMonth);
      const day = parseInt(dd, 10);
      const hour24 = parseInt(hh, 10);
      const ampm = hour24 >= 12 ? "PM" : "AM";
      const hour12 = hour24 % 12 || 12;
      return `${monthName} ${day}, ${yyyy} ${hour12}:${min} ${ampm}`;
    }
    // Fallback for non-ISO timestamps — use locale-safe explicit parsing
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return timestamp;
    const monthName = toTitleMonth(SHORT_MONTHS_LOWER[date.getMonth()]);
    const hour24 = date.getHours();
    const ampm = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 || 12;
    const min = String(date.getMinutes()).padStart(2, "0");
    return `${monthName} ${date.getDate()}, ${date.getFullYear()} ${hour12}:${min} ${ampm}`;
  } catch {
    return timestamp;
  }
};
